// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, from, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	catchError,
	filter,
	finalize,
	map,
	mapTo,
	startWith,
	switchMap,
	take,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs/operators';

import { AuthService } from 'src/app/services/auth.service';
import { AppState } from 'src/app/services/app-state.service';
import { titleFieldsMap } from 'src/app/app-data/constants/title-fields';
import { MaterialModule } from 'src/app/common/material/material-module';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { ICompleteSignUpParams, ICompleteSignUpResponse } from '@touchplan/common';
import { ParentErrorStateMatcher } from 'src/app/utils/parent-error-state-matcher';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { AccountCreationStore } from 'src/app/common/auth/services/account-creation-store.service';
import { SharedUtilityComponentsModule } from 'src/app/common/utility-components/shared-utility-components.module';
import { TenantMigrationNeeded } from '../../services/tenant-migration-needed.service';
import { GenericDialogModule } from 'src/app/common/generic-dialog/generic-dialog.module';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { AuthOutputs } from '../../services/auth-outputs.service';
import { FormAutoErrorComponent } from 'src/app/common/utility-components/form-auto-error.component';
import {
	emailFormatValidator,
	passwordFormatValidator,
	passwordMatchValidator,
} from 'src/app/common/form-utils/validators';
import { AuthLostMonitor } from 'src/app/services/background-scripts/auth-lost-monitor.service';
import { PasswordPolicyListComponent } from '../password-policy-list/password-policy-list.component';
import { DefaultLoggerService } from '@touchplan/logging';
import { UserFirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/user-cloud-functions.service';

interface ICompleteSignUpUserData extends ICompleteSignUpParams {
	password: string;
}

const SERVICE_TERMS_LINK = 'https://www.touchplan.io/terms-of-service';
const COMPONENT_IMPORTS = [
	CommonModule,
	MaterialModule,
	PresentationModule,
	ReactiveFormsModule,
	SharedUIMaterialModule,
	SharedUtilityComponentsModule,
	GenericDialogModule,
	FormAutoErrorComponent,
	PasswordPolicyListComponent,
];

@Component({
	selector: 'app-sign-up',
	standalone: true,
	imports: [...COMPONENT_IMPORTS],
	templateUrl: './sign-up.component.html',
	styleUrls: ['./sign-up.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [BannerMessageHandler, LoggerServiceFactory.provide('Auth Sign-Up Component')],
})
export class SignUpComponent implements OnInit, OnDestroy {
	protected appStrings = appStrings;
	public selectedValue: string;
	public titleFields$ = of(titleFieldsMap);
	public serviceTermsLink = SERVICE_TERMS_LINK;
	public passwordErrorMatcher = new ParentErrorStateMatcher();
	public confirmPasswordErrorMatcher = new ParentErrorStateMatcher(true);

	private destroy$ = new Subject<void>();

	private userDataOnlyIfAuthed$ = this.appState.waitForUser$;

	public initialLoading$ = this.userDataOnlyIfAuthed$.pipe(take(1), mapTo(false), startWith(true));

	public isAuthed$ = this.userDataOnlyIfAuthed$.pipe(
		map((user) => !!user),
		takeUntil(this.destroy$)
	);

	get passwords() {
		return this.signUpForm.get('passwords');
	}

	get password() {
		return this.passwords.get('password');
	}

	get confirmPassword() {
		return this.passwords.get('confirmPassword');
	}

	get email() {
		return this.signUpForm.get('email');
	}

	get title() {
		return this.signUpForm.get('title');
	}

	get terms() {
		return this.signUpForm.get('terms');
	}

	public signUpForm = this.formBuilder.group({
		firstName: ['', [Validators.required]],
		lastName: ['', [Validators.required]],
		company: ['', [Validators.required]],
		phone: ['', [Validators.required]],
		email: ['', [Validators.required, emailFormatValidator()]],
		title: ['', [Validators.required]],
		terms: ['', [Validators.requiredTrue]],
		passwords: this.formBuilder.group(
			{
				password: [''], // Validators set dynamically
				confirmPassword: [''], // Validators set dynamically
			},
			{ validators: passwordMatchValidator() }
		),
	});

	public onSave(): void {
		if (this.signUpForm.invalid) {
			this.terms.markAsDirty(); // to show the term error message if needed
			return;
		}

		const signUpFormData = {
			...this.signUpForm.getRawValue(),
			...this.passwords.getRawValue(),
		};
		this.accountCreationStore.collectSignupData(signUpFormData);
		this.accountCreationStore.accountData$
			.pipe(
				tap(() => this.accountCreationStore.setLoadingState(true)),
				take(1),
				withLatestFrom(this.appState.user),
				map(([accountData, user]) => ({
					userId: user?.$id,
					firstName: accountData.firstName,
					lastName: accountData.lastName,
					email: accountData.email,
					company: accountData.company,
					phone: accountData.phone,
					title: accountData.title,
					password: accountData.password,
					accessCode: accountData.accessCode,
				})),
				switchMap((accountData) => {
					if (accountData.userId) {
						return this.userFBCloudFunctionsService.completeSignUp(accountData);
					}
					return this.createUserAndCompleteSignUp(accountData);
				}),
				switchMap((result) => this.showFailedInvitesDialogIfAny(result)),
				//disable autoreload
				tap(() => this.authLostMonitor.disable()),
				switchMap(() => this.authService.forceTokenRefresh()),
				catchError((err) => {
					//setting the email on the auth record in "completeSignup" results in the token expiring due to a "major account change"
					// see https://firebase.google.com/docs/auth/admin/manage-sessions
					// so we force a token refresh to expose this state immediately, then specifically handle that error with
					// an inline re-auth by the user
					// Potential issue, there's some legacy auth code that refreshes the app 500ms after auth is lost
					// so the below error handling needs to resolve before that
					if (err.code === 'auth/user-token-expired') {
						this.logger.warn('Expected firebase auth/user-token-expired error', err);
						return this.accountCreationStore.tenant$.pipe(
							switchMap((tenant) => {
								return this.authOutputs.attemptSignupReauth(tenant);
							})
						);
					} else throw err;
				}),
				switchMap(() => this.tenantMigrationNeeded.forceEmailTenantRecheck()),
				finalize(() => this.accountCreationStore.setLoadingState(false))
			)
			.subscribe(
				() => {
					this.authOutputs.finishSignup();
				},
				(error) => {
					this.bannerMessageHandler.displayErrorMessage(error);
				}
			);
	}

	public onCancel(): void {
		this.authOutputs.cancelSignup(this.activatedRoute);
	}

	private createUserAndCompleteSignUp(accountData: ICompleteSignUpUserData) {
		return from(
			this.authService.createUserWithEmailAndPassword(accountData.email, accountData.password)
		).pipe(
			switchMap((userCredentials) => {
				// return EMPTY; //uncomment to force a partially created user
				return this.userFBCloudFunctionsService.completeSignUp({
					...accountData,
					userId: userCredentials.user?.uid,
				});
			})
		);
	}

	private prePopulateForm(): void {
		// Pre-populate Email from store
		this.accountCreationStore.email$
			.pipe(
				filter((email) => !!email),
				take(1)
			)
			.subscribe({
				next: (email) => {
					this.signUpForm.patchValue({ email });
					if (!!this.email?.value && !this.email.errors) {
						this.email?.disable();
					}
				},
				error: (error) => {
					this.bannerMessageHandler.displayErrorMessage(error);
				},
			});

		// Pre-populate user data
		this.userDataOnlyIfAuthed$
			.pipe(
				filter((user) => !!user),
				take(1),
				map((user) => ({
					userId: user.$id,
					phone: user.phone,
					firstName: user.firstName,
					lastName: user.lastName,
				}))
			)
			.subscribe(
				(userData) => {
					this.signUpForm.patchValue(userData);
				},
				(error) => {
					this.bannerMessageHandler.displayErrorMessage(error);
				}
			);
	}

	private setDynamicValidators(): void {
		this.isAuthed$.subscribe((isAuthed) => {
			if (!isAuthed) {
				this.password.addValidators([
					Validators.required,
					Validators.minLength(10),
					passwordFormatValidator(),
				]);
				this.confirmPassword.addValidators([Validators.required]);
			}
			this.password.updateValueAndValidity();
			this.confirmPassword.updateValueAndValidity();
		});
	}

	private showFailedInvitesDialogIfAny(result: ICompleteSignUpResponse): Observable<ICompleteSignUpResponse> {
		const data = result;
		const count = data?.invalidProjectsInvitations?.length;
		if (data?.invalidProjectsInvitations?.length) {
			const projectsNames = data.invalidProjectsInvitations.join(', ');
			const title = this.appStrings.SIGN_UP_ADDITION_TO_PROJECTS_FAILED_TITLE(count);
			const content = this.appStrings.SIGN_UP_ADDITION_TO_PROJECTS_FAILED_MESSAGE(count, projectsNames);
			const dialogRef = this.dialog.open(GenericDialogComponent, {
				data: {
					title,
					content: [content],
					yesButtonText: appStrings.UICONTROL_TEXT_OK,
				},
				disableClose: true,
			});
			return dialogRef.afterClosed().pipe(mapTo(result));
		}
		return of(result);
	}

	public ngOnInit() {
		this.accountCreationStore.setLoadingState(this.initialLoading$);

		this.prePopulateForm();
		this.setDynamicValidators();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	constructor(
		private logger: DefaultLoggerService,
		public appState: AppState,
		private formBuilder: FormBuilder,
		private authService: AuthService,
		private bannerMessageHandler: BannerMessageHandler,
		private userFBCloudFunctionsService: UserFirebaseCloudFunctionsService,
		protected accountCreationStore: AccountCreationStore,
		private tenantMigrationNeeded: TenantMigrationNeeded,
		private activatedRoute: ActivatedRoute,
		private dialog: MatDialog,
		private authOutputs: AuthOutputs,
		private authLostMonitor: AuthLostMonitor
	) {}
}
